import { mergeMap, catchError, switchMap, debounceTime } from 'rxjs/operators';
import { ofType } from 'redux-observable';
import get from 'lodash.get';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { of, EMPTY, merge, concat } from 'rxjs';

import { NotificationService, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import * as ShipmentsApi from '../../../store/shipments/shipments.api';
import * as ShipmentsActions from '../../../store/shipments/shipments.actions';
import Global from '../../../store/global-selectors';
import { ModalService } from 'ui-components/services/modal-service';
import * as BatchesGridApi from '../../../store/batches/grid/batches.grid.api';
import * as BatchesActions from '../../../store/batches/batches.actions';
import * as BatchPurchaseActions from '../../../store/batches/purchase/batches.purchase.actions';
import * as EntitiesActions from '../../../store/entities/entities.actions';
import * as OrderGridActions from '../../../store/orders/grid/orders.grid.actions';
import * as OrdersActions from '../../../store/orders/orders.actions';
import { orderGridRatesUpdated } from '../../../store/orders/grid/orders.grid.header.actions';
import * as OrderDetailsActions from '../../../store/orders/order-details/orders.details.actions';
import * as PrintActions from '../../printing/print.actions';
import * as CSWActions from '../../csw/csw.actions';
import { getOrdersSuccess } from '../../orders/orders.actions';
import { BatchDetailsHelper } from './batch-details-helper';
import { getIsOrdersPage } from '../../orders/orders.helpers';
import { getOrdersInShipmentsMode, getGroupByCounts } from '../../orders/orders.api';
import * as Actions from './batch.details.actions';
import * as Api from './batch.details.api';
import { mapOrdersFiltersToRequestBody } from '../../../services/filtering/api-param-map-service';
import { createLookupHash } from '../../../services/hash-service';
import { ColumnOrderByParamService } from '../../../services/column-orderby-param-service';
import { getPaginationDataFromLocation } from '../../../services/pagination-service';
import { getIsBatchDetailsPage } from '../batches.helpers';
import { cleanseFillIdsWithoutFulfillmentPlan } from '../../shipments/shipments.helpers';
import { messages } from '../messages';
import { getSelectedViewFromPathName } from '../../../pages/orders-page/orders-left-nav/orders-left-nav-context';
import { rateRefreshSubject } from '../../../fulfillment-planner/context';
import { pusherMessageToRateRefreshSubject } from '../../../fulfillment-planner/helpers/pusher-update-rates-mapper';

let notificationId;

export const getBatchDetailsEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.GET_BATCH_DETAILS_REQUEST, Actions.REMOVE_FULFILLMENT_PLANS_FROM_BATCH_SUCCESS),
    mergeMap((action) => {
      const fulfillmentPlanSetId = get(action, 'fulfillmentPlanSetId');
      if (!fulfillmentPlanSetId) {
        return EMPTY;
      }
      return Api.getBatchDetails(fulfillmentPlanSetId).pipe(
        mergeMap((batch) => {
          // NOTE: SPD-9235 - This is a ephemeral piece of logic that patches fulfillmentPlanSets within entities until we consolidate the logic to use entities.
          const fulfillmentPlanSetEntities = [
            {
              fulfillmentPlanSetId: batch.fulfillmentPlanSetId,
              fulfillmentPlanSetName: batch.name,
            },
          ];
          return concat(
            of(Actions.getBatchDetailsSuccess(batch)),
            of(
              EntitiesActions.entitiesBulkload({
                fulfillmentPlanSets: createLookupHash(fulfillmentPlanSetEntities, 'fulfillmentPlanSetId'),
              }),
            ),
          );
        }),
        catchError(() => EMPTY),
      ); // TODO: handle error case
    }),
  );
};

export const validateBatchAddressesEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.VALIDATE_BATCH_ADDRESSES_REQUEST),
    mergeMap((action) => {
      const { processFulfillmentPlanSet, fulfillmentPlanSetId, fulfillmentPlanIds } = action;
      if (!fulfillmentPlanSetId || !fulfillmentPlanIds) {
        return EMPTY;
      }
      return BatchesGridApi.validateBatchesAddresses(processFulfillmentPlanSet, fulfillmentPlanSetId, fulfillmentPlanIds).pipe(
        mergeMap((batchData) => {
          return of(Actions.validateBatchAddressesSuccess(batchData));
        }),
        catchError((e) => of(Actions.validateBatchAddressesFailure(e))),
      );
    }),
  );
};

export const getBatchFulfillmentPlansForGridEpic = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(
      Actions.GET_BATCH_DETAILS_SUCCESS,
      Actions.BATCH_ADDRESS_VALIDATION_COMPLETED,
      Actions.REMOVE_FULFILLMENT_PLANS_FROM_BATCH_SUCCESS,
      Actions.RATE_BATCH_COMPLETED,
      Actions.BATCH_DETAILS_RELOAD,
      BatchPurchaseActions.CREATE_LABELS_FOR_BATCHES_SUCCESS,
      BatchesActions.ADDED_FULFILLMENT_PLANS_TO_CREATED_BATCH_SUCCESS,
    ),
    mergeMap((action) => {
      const state = getState();
      const pagingStats = getPaginationDataFromLocation();
      const { fulfillmentPlanSetId, appliedFilters } = BatchDetailsHelper.getBatchDetailsRequestParams(state, action, history);
      if (!fulfillmentPlanSetId || !pagingStats) {
        return EMPTY;
      }

      const isOnBatchesPage = getIsBatchDetailsPage();
      if (isOnBatchesPage) {
        return of(Actions.getBatchDetailsOrdersShipmentsMode(fulfillmentPlanSetId));
      }

      // const filter, orderBy, orderByDirection, -- to be calculated like in shipments epic, I would imagine
      const { page: pagingStatsPage, pageSize: pagingStatsPageSize } = pagingStats;
      return ShipmentsApi.getShipmentsSimple({
        page: pagingStatsPage,
        pageSize: pagingStatsPageSize,
        filter: {
          collectionFilters: [{ column: 'FulfillmentPlanSetId', values: [fulfillmentPlanSetId] }],
        },
      }).pipe(
        mergeMap((data) => {
          const { page, pageSize, totalCount, currentPageFulfillmentIds, fulfillmentPlans, fulfillments } = data;

          // this is to filter out fulfillmentIds that have no fulfillmentPlanId attached, causes insidious bugs later
          const cleansedcurrentPageFulfillmentIds = cleanseFillIdsWithoutFulfillmentPlan({
            currentPageFulfillmentIds,
            fulfillments,
            fulfillmentPlans,
          });

          const actionCollection = [
            of(ShipmentsActions.getShipmentsSuccess([], page, pageSize, totalCount, cleansedcurrentPageFulfillmentIds)),
            of(Actions.getFilteredBatchShipmentsSuccess(appliedFilters)),
          ];
          return concat(...actionCollection);
        }),
        catchError((error) => of(ShipmentsActions.getShipmentsFailure(error))),
      );
    }),
  );
};

export const getBatchOrdersShipmentsMode = (action$, { getState }, { history }) => {
  return action$.pipe(
    ofType(Actions.BATCH_DETAILS_GET_ORDERS_SHIPMENTS_MODE),
    mergeMap(({ fulfillmentPlanSetId, appliedFilters }) => {
      const state = getState();
      let fulfillmentPlanSetIdWithFallback = fulfillmentPlanSetId;
      if (!fulfillmentPlanSetId) {
        fulfillmentPlanSetIdWithFallback = Global.selectBatchDetailsFulfillmentPlanSetId(state);
      }
      let appliedFiltersWithFallback = appliedFilters;
      if (!appliedFilters) {
        appliedFiltersWithFallback = Global.selectBatchDetailsAppliedFilters(state);
      }
      const sellerTimeZone = Global.selectSellerTimeZoneIANA(state);
      const filterRequestJson = mapOrdersFiltersToRequestBody(appliedFiltersWithFallback, sellerTimeZone);
      const isOrdersPage = getIsOrdersPage();

      const stores = Global.selectSellerActiveStores(state);
      const statusViewId = getSelectedViewFromPathName(history?.location?.pathname, stores)?.id;
      const activeFilterView = Global.selectOrdersFilteringActiveFilterView(state);
      const activeFilterViewId = activeFilterView.filterViewId;
      const orderByColumn = Global.selectOrdersGridCurrentOrderByColumn(state, {
        statusViewId,
        activeFilterViewId,
      });
      const orderByParam = ColumnOrderByParamService.getOrdersGridOrderByApiParameterOrDefault(orderByColumn, true);
      const selectedGroupByEnums = Global.selectOrdersGridActiveGroupByEnums(state, { statusViewId, activeFilterViewId });
      const groupByParams = isOrdersPage ? ColumnOrderByParamService.getOrdersGridOrderByParamsFromGroupByEnums(selectedGroupByEnums) : [];
      const allOrderBys = [...groupByParams, orderByParam];
      const dedupedOrderByParams = {};
      allOrderBys.forEach((param) => (dedupedOrderByParams[param.orderBy] = param));
      const request = {
        filter: {
          fulfillmentPlanSetId: fulfillmentPlanSetIdWithFallback,
          ...filterRequestJson,
        },
        orderBys: Object.values(dedupedOrderByParams),
      };
      const getOrderFailureAction$ = (error) =>
        merge(
          of(Actions.setIsLoading(false)),
          of(OrderGridActions.orderGridSetIsGroupBysCountLoading(false)),
          of(OrdersActions.getOrdersFailure(error)),
        );
      const pagingStats = getPaginationDataFromLocation();
      const groupByCounts$ =
        groupByParams.length === 0
          ? EMPTY
          : getGroupByCounts(undefined, request.filter, groupByParams).pipe(
              mergeMap((countsArr) => of(OrderGridActions.orderGridSetGroupBysCount(countsArr))),
              catchError((error) => getOrderFailureAction$(error)),
            );

      const getOrders$ = getOrdersInShipmentsMode(undefined, pagingStats.page, pagingStats.pageSize, request).pipe(
        mergeMap(({ currentPageFulfillmentPlanIds, page, pageSize, totalCount }) => {
          return concat(
            of(Actions.setIsLoading(false)),
            of(OrderGridActions.orderGridSetIsGroupBysCountLoading(false)),
            of(getOrdersSuccess(currentPageFulfillmentPlanIds, page, pageSize, totalCount)),
            of(Actions.getFilteredBatchShipmentsSuccess(appliedFiltersWithFallback)),
          );
        }),
        catchError((error) => getOrderFailureAction$(error)),
      );
      return merge(getOrders$, groupByCounts$);
    }),
  );
};

export const removedFulfillmentPlansFromBatchEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.REMOVE_FULFILLMENT_PLANS_FROM_BATCH),
    mergeMap((action) => {
      const { fulfillmentPlanSetId, fulfillmentPlanIds } = action;

      return Api.removeFulfillmentPlansFromBatch(fulfillmentPlanSetId, fulfillmentPlanIds).pipe(
        mergeMap(() => {
          return of(Actions.removedFulfillmentPlansFromBatchSuccess(fulfillmentPlanSetId));
        }),
        catchError(() => EMPTY),
      );
    }),
  );
};

export const closeMoveToBatchModalEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(
      BatchesActions.ADDED_FULFILLMENT_PLANS_TO_CREATED_BATCH_SUCCESS,
      BatchesActions.ADD_SALES_ORDERS_TO_BATCH_SUCCESS,
      BatchesActions.ADDED_SALES_ORDERS_TO_CREATED_BATCH_SUCCESS,
    ),
    mergeMap(() => {
      const modalId = Global.selectBatchDetailsMoveToBatchModalId(getState());
      ModalService.closeModalById(modalId);
      return of(OrderDetailsActions.markOrderAsModified());
    }),
  );
};

export const batchValidationCompletedEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.VALIDATE_BATCH_ADDRESSES_RESULTS),
    debounceTime(500),
    switchMap((action) => {
      const jobId = get(action, 'data.jobId');
      const batchAddressValidationJob = Global.selectBatchDetailsAddressValidationJobs(getState()).find((job) => job.jobId === jobId);
      if (batchAddressValidationJob && batchAddressValidationJob.isDone) {
        return of(Actions.triggerBatchAddressValidationCompleted());
      }
      return EMPTY;
    }),
  );
};

export const rateBatchCompletedEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.RATES_UPDATED_RESULTS),
    debounceTime(500),
    switchMap((action) => {
      const state = getState();
      const jobId = get(action, 'data.jobId');
      const rateBatchJob = Global.selectBatchDetailsRateBatchJobs(state).find((job) => job.jobId === jobId);

      if ((rateBatchJob && rateBatchJob.isDone) || action?.data?.final) {
        return concat(
          of(Actions.triggerRateBatchCompleted()),
          of(
            orderGridRatesUpdated({
              ...action?.data,
              forceRefresh: action?.data?.final,
            }),
          ),
        );
      }
      return EMPTY;
    }),
  );
};

export const rateOpenedOrderFromBatchEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.RATES_UPDATED_RESULTS),
    switchMap((action) => {
      const orders = action?.data?.Orders;
      if (!orders?.length) {
        return EMPTY;
      }
      const code = Global.selectSellerDefaultCurrency(getState());

      orders.forEach((order) => {
        rateRefreshSubject.next({
          fulfillmentPlanId: order.OrderID?.toString(),
          isRefreshingRate: false,
          rateObject: pusherMessageToRateRefreshSubject(order, code),
        });
      });

      return EMPTY;
    }),
  );
};

export const sendShipmentNotificationsEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.SEND_SHIPMENT_NOTIFICATIONS),
    mergeMap((action) => {
      const fulfillmentPlanIds = get(action, 'fulfillmentPlanIds');
      if (fulfillmentPlanIds) {
        const notificationProps = {
          title: <FormattedMessage {...messages.sendEmailsTitle} />,
          content: <FormattedMessage {...messages.sendEmailsContent} />,
          notificationType: 'info',
          dontHide: true,
        };
        notificationId = NotificationService.showNotification(notificationProps);
        return Api.sendShipmentNotifications(fulfillmentPlanIds).pipe(
          mergeMap((jobs) => of(Actions.sendShipmentNotificationsSuccess(jobs))),
          catchError(() => EMPTY),
        );
      }
      return EMPTY;
    }),
  );
};

export const shipmentNotificationPusherMessageEpic = (action$, { getState }) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_NOTIFICATION_MESSAGE),
    mergeMap((action) => {
      const jobGuid = get(action, 'jobGuid', '');
      const allCurrentJobs = Global.selectBatchDetailsShipmentNotificationJobs(getState());
      if (allCurrentJobs.includes(jobGuid)) {
        const message = get(action, 'message.data.errorMessage', '');
        const isSuccess = get(action, 'message.data.isSuccess', false);
        if (isSuccess) {
          return of(Actions.shipmentNotificationsSentSuccessfully(jobGuid));
        }
        return of(Actions.shipmentNotificationsSentFailure(jobGuid, message));
      }
      return EMPTY;
    }),
  );
};

export const shipmentNotificationsSentSuccessfullyEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_NOTIFICATIONS_SENT_SUCCESS),
    mergeMap(() => {
      NotificationService.removeNotification(notificationId);
      const notificationProps = {
        title: <FormattedMessage {...messages.emailsSentTitle} />,
        content: <FormattedMessage {...messages.emailsSentContent} />,
        notificationType: 'info',
        duration: 3000,
      };
      NotificationService.showNotification(notificationProps);
      return EMPTY;
    }),
  );
};

export const shipmentNotificationsSentFailureEpic = (action$) => {
  return action$.pipe(
    ofType(Actions.SHIPMENT_NOTIFICATIONS_SENT_FAILURE),
    mergeMap((action) => {
      const errorMessage = get(action, 'errorMessage', '');
      NotificationService.removeNotification(notificationId);
      const notificationProps = {
        title: <FormattedMessage {...messages.emailsSentTitle} />,
        content: errorMessage,
        notificationType: NOTIFICATION_TYPE_ERROR,
        duration: 5000,
      };
      NotificationService.showNotification(notificationProps);
      return EMPTY;
    }),
  );
};

export const refreshBatchDetails = (action$) => {
  return action$.pipe(
    ofType(
      PrintActions.MARK_PACKING_SLIP_PRINTED_SUCCESS,
      PrintActions.MARK_PICK_LIST_PRINTED_SUCCESS,
      PrintActions.MARK_LABEL_PRINTED_SUCCESS,
      CSWActions.CSW_CREATE_LABEL_SUCCESS,
    ),
    mergeMap(() => {
      if (getIsBatchDetailsPage()) {
        return of(Actions.getBatchDetailsOrdersShipmentsMode());
      }

      return EMPTY;
    }),
  );
};
